import {formatInTimeZone} from 'date-fns-tz'
import differenceInYears from 'date-fns/differenceInCalendarYears'
import differenceInDays from 'date-fns/differenceInDays'
import differenceInHours from 'date-fns/differenceInHours'
import differenceInMinutes from 'date-fns/differenceInMinutes'
import format from 'date-fns/format'
import formatDistance from 'date-fns/formatDistance'
import parseISO from 'date-fns/parseISO'
import React, {useEffect, useState} from 'react'
import {parseISODuration} from '../common/time/duration'
import {IntervalInput} from '../data/workbench-api/generated/types'

export const formatTime = (date: Date, uppercase?: boolean) => {
  const d = format(date, 'p').replace(/ \s(AM|PM)/, '$1')
  return uppercase ? d : d.toLowerCase()
}

const useCurrentTime = () => {
  const [now, setNow] = useState<Date>(new Date())

  // refresh periodically to keep it current
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date())
    }, 10000)
    return () => clearInterval(interval)
  }, [])

  return now
}

export const savedFormat = (date: Date, now: Date) => {
  const minutes = differenceInMinutes(now, date)

  if (minutes <= 0) {
    return 'just now'
  }
  if (minutes === 1) {
    return '1 min ago'
  }
  if (minutes < 60) {
    return `${minutes} mins ago`
  }

  const days = differenceInDays(now, date)

  if (days < 1) {
    return `at ${formatTime(date)}`
  }
  if (days < 7) {
    return `on ${format(date, 'EEEE,')} ${formatTime(date)}`
  }

  const years = differenceInYears(now, date)

  if (years < 1) {
    return `on ${format(date, 'd MMM,')} ${formatTime(date)}`
  }

  return `on ${format(date, 'd MMM yyyy,')} ${formatTime(date)}`
}

interface ElapsedTimeProps {
  date: string
}

export const ElapsedTime = ({date}: ElapsedTimeProps) => {
  const now = useCurrentTime()
  try {
    return <>{formatDistance(now, parseISO(date)).replace(/^about /, '')} ago</>
  } catch (e) {
    console.error(`Unable to format elapsed time`, now, date)
    return null
  }
}

interface ElapsedAbsoluteTimeProps {
  date?: string | Date | null
  lowerCase?: boolean
  ago?: boolean
  utc?: boolean
}

export const ElapsedAbsoluteTime = ({
  date,
  lowerCase,
  ago = true, // give me "1(h|m|d) ago" etc
  utc = false
}: ElapsedAbsoluteTimeProps) => {
  const now = useCurrentTime()

  const formatFn = utc ? formatUTC : format

  if (!date) {
    return null
  }

  const dateObject = new Date(typeof date === 'string' ? parseISO(date) : date)

  try {
    const minutes = differenceInMinutes(now, dateObject)

    if (ago) {
      if (minutes <= 0) {
        return lowerCase ? <>just now</> : <>Just now</>
      }

      if (minutes === 1) {
        return <>1m ago</> // 1m ago
      }

      if (minutes < 60) {
        return <>{`${minutes}m ago`}</> // 59m ago
      }

      const hours = differenceInHours(now, dateObject)

      if (hours === 1) {
        return <>1h ago</> // 1h ago
      }

      if (hours < 24) {
        return <>{`${hours}h ago`}</> // 11h ago
      }
    }

    const days = differenceInDays(now, dateObject)
    // can be less than one days difference, but still different dates
    const isSameDay = now.getDate() === dateObject.getDate()

    // If we don't check isSameDay then when ago=false we get false results here
    // where yesterday shows as "today" - not seen when ago=true due to early return
    if (days === 0 && isSameDay) {
      return lowerCase ? (
        <>today, {format(dateObject, 'h:mm a')}</>
      ) : (
        <>Today, {format(dateObject, 'h:mm a')}</>
      ) // Today, 11:45 PM
    }

    if (days === 1 || (days === 0 && !isSameDay)) {
      return lowerCase ? (
        <>yesterday, {format(dateObject, 'h:mm a')} </>
      ) : (
        <>Yesterday, {format(dateObject, 'h:mm a')} </>
      ) // Yesterday, 11:45 PM
    }

    if (days > 1 && days < 7) {
      return <>{formatFn(dateObject, 'EEEE, h:mm a')}</> // Friday, 11:45 PM
    }

    const years = differenceInYears(now, dateObject)

    if (years < 1) {
      return <>{formatFn(dateObject, 'h:mm a, EEE d MMM')}</> // 11:45 PM, Fri 30 Dec
    }

    return <>{formatFn(dateObject, 'h:mm a, EEE d MMM, yyyy')}</> // 11:45 PM, Fri 30 Dec, 2020
  } catch (e) {
    console.error(`Unable to format elapsed absolute time`, now, date)
    return null
  }
}

const formatUTC = (date: Date, fmt: string) =>
  formatInTimeZone(date, 'UTC', fmt)

const DURATION_MAPPING: Record<keyof Duration, string> = {
  years: 'y',
  months: 'mo',
  weeks: 'w',
  days: 'd',
  hours: 'h',
  minutes: 'm',
  seconds: 's'
}

export const formatDuration = (duration: Duration) => {
  const formatted: string[] = []
  for (const property of Object.keys(DURATION_MAPPING) as (keyof Duration)[]) {
    if (duration[property]) {
      formatted.push(duration[property] + DURATION_MAPPING[property])
    }
  }
  return formatted.join(' ')
}

export const formatISOInterval = (period: string) => {
  const duration = parseISODuration(period)
  if (!duration) {
    return ''
  }
  return formatDuration(duration)
}

export const durationToIsoInterval = (duration?: IntervalInput) => {
  if (!duration) {
    return undefined
  }

  const date: (keyof IntervalInput)[] = ['years', 'months', 'days']
  const time: (keyof IntervalInput)[] = ['hours', 'minutes', 'seconds']

  const append = (iso: string, key: keyof IntervalInput) => {
    if (duration[key]) {
      return `${iso}${duration[key]}${key[0].toUpperCase()}`
    }
    return iso
  }

  const dateISO = date.reduce((iso, key) => append(iso, key), 'P')
  const timeStr = time.reduce((iso, key) => append(iso, key), '')

  const timeISO = timeStr ? `T${timeStr}` : ''

  return `${dateISO}${timeISO}` || undefined
}

export const apiIntervalToDuration = (interval: IntervalInput): Duration => {
  return {
    years: interval.years ?? undefined,
    months: interval.months ?? undefined,
    days: interval.days ?? undefined,
    hours: interval.hours ?? undefined,
    minutes: interval.minutes ?? undefined,
    seconds: interval.seconds ?? undefined
  }
}

export const durationToInterval = (
  duration: Record<string, string | number | null>
): Pick<IntervalInput, 'days' | 'hours' | 'minutes'> => {
  const valid = ['days', 'hours', 'minutes']
  return Object.entries(duration).reduce(
    (interval: IntervalInput, [k, v]) => ({
      ...interval,
      ...(valid.includes(k)
        ? {[k]: v === null || v === '' ? null : parseInt(String(v))}
        : {})
    }),
    {}
  )
}

interface SavedTimeProps {
  date: string
}

export const SavedTime = ({date}: SavedTimeProps) => {
  const now = useCurrentTime()
  return <>{savedFormat(parseISO(date), now)}</>
}

export const standardDateTime = (date: string | Date) =>
  format(typeof date === 'string' ? parseISO(date) : date, 'd MMM, h:mm a')

interface StandardDateProps {
  date: string
}
/** @deprecated use standardDateTime */
export const StandardDateTime = ({date}: StandardDateProps) => (
  <>{standardDateTime(date)}</>
)

export const changeInTime = (
  // we get back milliseconds from the api, so accounting for that in the type
  duration: (Duration & {milliseconds?: number}) | undefined | null
) => {
  if (!duration) return null

  const formatted: string[] = []
  const props: [keyof Duration, string][] = [
    ['years', 'y'],
    ['months', 'M'],
    ['weeks', 'w'],
    ['days', 'd'],
    ['hours', 'h'],
    ['minutes', 'm']
  ]

  for (const [prop, suffix] of props) {
    if (duration[prop]) {
      formatted.push(duration[prop] + suffix)
    }
    // general case, show two dimensions max
    if (formatted.length >= 2) {
      break
    }
  }

  // special case for seconds, only show if less than a minute
  if (duration.seconds && !formatted.length) {
    formatted.push(duration.seconds + 's')
  }

  return formatted.length === 0 ? 'Less than a second' : formatted.join(' ')
}

export const formatDateRange = (
  from: Date,
  to: Date,
  now: Date = new Date()
) => {
  if (
    from.getFullYear() !== to.getFullYear() ||
    from.getFullYear() !== now.getFullYear()
  ) {
    return `${format(from, 'EEE d MMM YYY, h:mm a')} – ${format(
      to,
      'EEE d MMM YYY, h:mm a'
    )}`
  }

  return `${format(from, 'EEE d MMM, h:mm a')} – ${format(
    to,
    'EEE d MMM, h:mm a'
  )}`
}

export const formatByteSize = (sizeInBytes: number, places = 1) => {
  if (!sizeInBytes) {
    return '0 bytes'
  }

  const sizes = ['byte', 'kilobyte', 'megabyte', 'gigabyte']

  const unit = Math.min(
    Math.floor(Math.log(sizeInBytes) / Math.log(1024)),
    sizes.length - 1
  )

  return new Intl.NumberFormat(['en'], {
    style: 'unit',
    unit: sizes[unit],
    unitDisplay: 'short',
    maximumFractionDigits: places
  }).format(sizeInBytes / 1024 ** unit)
}
